import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { formatDate } from "client_legacy/ndaw/dateFormatting";
import { OngoingOrNextAppointmentClientModel } from "client/Api/Notifications/notifications";
import { Anchor, DateString, Icon, Space } from "client/Components";
import { ActivityCard, ActivityCardIconKind } from "../../Activity/Card/ActivityCard";
import { ActivityLayout } from "../../Activity/Layout/ActivityLayout";
import { OnlineMeetingButton } from "client/Features/Calendar/OnlineMeetingButton";
import styles from "./AppointmentActivity.module.less";
import {
	calculateDayDifference,
	calculateTimeDifference,
	getIsToday,
	isBetween,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { useCalendarClient } from "client/Features/Calendar/useCalendarClient";
import { formatCalendarLocations } from "client/Features/Calendar/calendarUtils";

const {
	SubText,
	Title
} = ActivityLayout;

const {
	Description
} = ActivityCard;

export type AppointmentActivityProps = {
	appointment: OngoingOrNextAppointmentClientModel;
};

export const AppointmentActivity = (props: AppointmentActivityProps): ReactElement | null => {
	const {
		appointment
	} = props;

	const {
		id,
		appIcon,
		color,
		subject: originalSubject,
		locations,
		meeting
	} = appointment;

	const now = new Date();
	const start: Date = parseDtoDate(appointment.start);
	const end: Date = parseDtoDate(appointment.end);
	const subject: string = originalSubject || localize("_NoSubject");
	const location: string | null = formatCalendarLocations(locations);

	const [calendarClientElement, calendarClient] = useCalendarClient({});

	const userTimezone: TimeZone = useUserTimezone();

	const openDetailsHandler = useCallback(
		(): void => {
			if (!calendarClient.doesSupportDetails) {
				console.error("Calendar client does not support opening of the details.");
				return;
			}

			calendarClient.openDetails({
				appointmentIdentifier: { id }
			});
		},
		[calendarClient, id]
	);

	const hasAlreadyEnded: boolean = end.getTime() <= now.getTime();
	if (hasAlreadyEnded) {
		return null;
	}

	const timeLabel: string = getAppointmentTimeLabel(
		start,
		end,
		now,
		userTimezone
	);

	const renderDateRange = (): ReactElement => {
		const meetingDurationInDays: number = calculateDayDifference(
			end,
			start,
			userTimezone,
			true
		);

		if (meetingDurationInDays < 1) {
			return (
				<>
					<DateString
						value={start}
						format="T"
						timezone={userTimezone}
					/>
					<Space /> - <Space />
					<DateString
						value={end}
						format="T"
						timezone={userTimezone}
					/>
				</>
			);
		} else {
			return (
				<>
					<DateString
						value={start}
						format="T"
						timezone={userTimezone}
					/>
					<Space /> - <Space />
					{meetingDurationInDays.toFixed(1)} {localize("_Days")}
				</>
			);
		}
	};

	return (
		<div className={styles.root}>
			{timeLabel && (
				<span className={styles.timeLabel}>
					<span className={styles.timeLabelIcon}>
						<Icon value="clock" size="small" />
					</span>
					{timeLabel}
				</span>
			)}
			<Anchor onClick={openDetailsHandler}>
				<ActivityCard
					categoryName={localize("_CalendarEvent")}
					color={color}
					isDismissed
					icon={{
						isTransparent: false,
						kind: ActivityCardIconKind.AppIcon,
						appIcon
					}}
				>
					<Description>
						<ActivityLayout>
							<Title>
								<span
									title={subject}
									className={styles.subject}
								>
									{subject}
								</span>
							</Title>
							<SubText>
								<DateString
									value={start}
									format="RelativeDay"
									timezone={userTimezone}
								/>
							</SubText>
							<SubText>{renderDateRange()}</SubText>
							{location && (
								<SubText>
									<span title="location">{location}</span>
								</SubText>
							)}
						</ActivityLayout>
						{meeting && (
							<div className={styles.join}>
								<OnlineMeetingButton
									url={meeting.url}
									product={meeting.product}
									trackingId="appointment-activity__join-meeting"
								/>
							</div>
						)}
					</Description>
				</ActivityCard>
			</Anchor>
			{calendarClientElement}
		</div>
	);
};

AppointmentActivity.displayName = "AppointmentActivity";

function getAppointmentTimeLabel(
	start: Date,
	end: Date,
	now: Date,
	timezone: TimeZone
): string {
	const isOngoing: boolean = isBetween(
		now,
		start,
		end
	);

	if (isOngoing) {
		return localize("_Now");
	}

	const isToday: boolean = getIsToday(start, timezone);

	if (isToday) {
		const minutesTillStart: number = Math.ceil(
			calculateTimeDifference(
				start,
				now,
				"minute",
				true
			)
		);

		if (minutesTillStart === 1) {
			return localize("_In1Minute");
		} else if (minutesTillStart < 60) {
			return localize("_InAmountMinutes")
				.replace("{{Amount}}", minutesTillStart.toString());
		} else {
			return formatDate(start, "T", timezone);
		}
	}

	return localize("_UpNext");
}